import React, { useEffect, useState } from 'react';
import format from 'date-fns/format';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SuccessIcon from '../../../assets/lotties/checkmark_success_animation.json';
import Lottie from '../../../generic-components/Lottie';
import { ReactComponent as Download } from '../../../assets/svg/download.svg';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import { takeSnapshot } from '../../../services/snapshot.service';
import { useStore } from '../../../store';
import { STEPS, STYLES } from '../consts';
import { VIEWS_MAP } from '../../../router/constants';
import { SUPPLIER_REFERRER_TYPES } from '../../Supplier/consts';
import { BASE_URL } from '../../../constants/endpoints';
import Icon from '../../../generic-components/Icon';
import CloseIcon from '../../../assets/svg/x-icon-v2.svg';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { getAddressDetails } from '../../Supplier/helpers';
import { UserMessage } from '../../../DesignSystem/LiliUserMessage/UserMessage';
import { trackMixPanel } from '../../../services/mixpanel.service';

export const Success = () => {
  const {
    resetMailCheckSlice,
    setActiveStep,
    checkAmount,
    payeeDetails,
    feeData,
    setSelectedLimit,
    isMailCheckMemoAdded,
  } = useStore((state) => state);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSnapshootMode, setIsSnapshootMode] = useState<boolean>(false);

  const handleMailAnotherCheck = () => {
    trackMixPanel('Act on Move Money Confirmation Screen', { Action: 'Mail Another Check' });
    resetMailCheckSlice();
    setActiveStep(STEPS.INTRO);
    navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.PICKER}`, {
      state: { referrer: SUPPLIER_REFERRER_TYPES.MAIL_CHECK, closeUrl: `/${VIEWS_MAP.MAIL_A_CHECK}` },
    });
  };

  const handleDone = () => {
    trackMixPanel('Act on Move Money Confirmation Screen', { Action: 'Done' });
    resetMailCheckSlice();
    setActiveStep(STEPS.LOADER);
    navigate(`${BASE_URL}/${VIEWS_MAP.DASHBOARD}`);
  };

  const handleDownload = () => {
    trackMixPanel('Act on Move Money Confirmation Screen', { Action: 'Download' });
    setIsSnapshootMode(true);
    setTimeout(() => {
      takeSnapshot('shareCardDetails', true).then((url) => {
        const elLink = document.createElement('a');
        elLink.setAttribute('href', url as string);
        elLink.setAttribute('download', `mailCheck-${format(new Date(), 'MMM dd, yyyy')}`);
        elLink.click();
        setIsSnapshootMode(false);
      });
    }, 150);
  };

  useEffect(() => {
    trackMixPanel('View Move Money Confirmation Screen', {
      'Memo Added': isMailCheckMemoAdded,
      Amount: Number(checkAmount),
      'Fee Paid': feeData.fee,
      'Estimated Arrival': feeData.arrivalDate,
      Flow: 'Mail a Check',
    });
    return () => {
      setSelectedLimit({
        key: '',
        value: 0,
      });
    };
  }, []);

  return (
    <>
      {isSnapshootMode && (
        <UserMessage onClick={() => {}} id='downloadWrapperModal'>
          <div
            id='shareCardDetails'
            className='flex flex-col items-center justify-center py-[24px] px-[24px] bg-bw02 rounded-[8px]'
          >
            <Heading
              title={t('MAIL_CHECK.SUCCESS.TITLE')}
              className='font-metricR text-bw08 text-[18px] mb-[8px] leading-[150%]'
            />
            <div className='bg-bw00 rounded-[8px] border border-bw03 w-[464px] flex flex-col mb-[16px]'>
              <div className='flex flex-col items-center py-[18px] px-[10px] border-b border-bw03'>
                <Heading
                  title={t('MAIL_CHECK.SUCCESS.AMOUNT')}
                  className='font-metricR text-[16px] text-bw07 leading-[150%]'
                />
                <Heading
                  title={convertValueToUSD(checkAmount, 2)}
                  className='font-metricR text-[26px] text-action03 leading-[150%]'
                />
              </div>
              <div className='flex flex-col items-center py-[18px] px-[10px] border-b border-bw03'>
                <Heading
                  title={payeeDetails?.name || ''}
                  className='font-metricRSemibold text-[16px] text-bw08 leading-[150%]'
                />
                {payeeDetails?.supplierAddressData && (
                  <Heading
                    title={getAddressDetails(payeeDetails?.supplierAddressData)}
                    className='font-metricR text-[16px] text-bw07 leading-[150%] max-w-[200px] text-center'
                  />
                )}
              </div>
              <div className='flex items-center justify-between py-[18px] px-[16px] border-b border-bw03'>
                <Heading
                  title={t('MAIL_CHECK.SUCCESS.ESTIMATE_ARRIVAL')}
                  className='font-metricR text-[16px] text-bw07 leading-[150%]'
                />
                <Heading title={feeData.arrivalDate} className='font-metricR text-[16px] text-bw08 leading-[150%]' />
              </div>
              <div className='flex items-center justify-center py-[18px] px-[16px] bg-bw01 rounded-b-[8px]'>
                <Heading
                  title={`${t('MAIL_CHECK.SUCCESS.TRANSACTION_DATE')} ${format(new Date(), 'MMM dd, yyyy')}`}
                  className='font-metricR text-bw07 text-[14px]'
                />
              </div>
            </div>
          </div>
        </UserMessage>
      )}
      <div className='w-full py-[14px] px-[34px] flex items-center justify-between border-b border-bw03'>
        <Button
          className='rounded-[50%] hover:bg-bw02 py-[4px] px-[4px] flex items-center justify-center'
          id=''
          childElement={<Icon onClick={handleDone} className={STYLES.AMOUNT.HEADER_ICON} src={CloseIcon} />}
        />
        <Heading title={t('MAIL_CHECK.AMOUNT.HEADER_TITLE')} className={STYLES.AMOUNT.HEADER_TITLE} />
        <span className='w-[24px]' />
      </div>
      <div className='w-full grow relative bg-bw02'>
        <div className='absolute inset-0 flex flex-col items-center overflow-y-scroll py-[22px]'>
          <Lottie lottieAsset={SuccessIcon} isLoop={false} isAutoplay className='w-[50px] mb-[10px]' />
          <Heading
            title={t('MAIL_CHECK.SUCCESS.TITLE')}
            className='font-metricR text-bw08 text-[18px] leading-[150%]'
          />
          <Heading
            type='HTML'
            title={t('MAIL_CHECK.SUCCESS.SUB_TITLE')}
            className='text-[16px] text-bw07 font-metricR text-center mb-[18px]'
          />
          <div className='bg-bw00 rounded-[8px] border border-bw03 w-[464px] flex flex-col mb-[16px]'>
            <div className='flex flex-col items-center py-[18px] px-[10px] border-b border-bw03'>
              <Heading
                title={t('MAIL_CHECK.SUCCESS.AMOUNT')}
                className='font-metricR text-[16px] text-bw07 leading-[150%]'
              />
              <Heading
                title={convertValueToUSD(checkAmount, 2)}
                className='font-metricRSemibold text-[26px] text-action03 leading-[150%]'
              />
            </div>
            <div className='flex flex-col items-center py-[18px] px-[10px] border-b border-bw03'>
              <Heading
                title={payeeDetails?.name || ''}
                className='font-metricRSemibold text-[16px] text-bw08 leading-[150%]'
              />
              {payeeDetails?.supplierAddressData && (
                <Heading
                  title={getAddressDetails(payeeDetails?.supplierAddressData)}
                  className='font-metricR text-[16px] text-bw07 leading-[150%] max-w-[200px] text-center'
                />
              )}
            </div>
            <div className='flex items-center justify-between py-[18px] px-[16px] border-b border-bw03'>
              <Heading
                title={t('MAIL_CHECK.SUCCESS.ESTIMATE_ARRIVAL')}
                className='font-metricR text-[16px] text-bw07 leading-[150%]'
              />
              <Heading title={feeData.arrivalDate} className='font-metricR text-[16px] text-bw08 leading-[150%]' />
            </div>
            <div className='flex items-center justify-between py-[18px] px-[16px] border-b border-bw03'>
              <Heading
                title={t('MAIL_CHECK.SUCCESS.TOTAL_FEE')}
                className='font-metricR text-[16px] text-bw07 leading-[150%]'
              />
              <Heading
                title={convertValueToUSD(feeData.fee, 2)}
                className='font-metricR text-[16px] text-bw08 leading-[150%]'
              />
            </div>
            <div className='flex items-center justify-center py-[11px] px-[16px] bg-bw01 rounded-b-[8px]'>
              <Heading
                title={`${t('MAIL_CHECK.SUCCESS.TRANSACTION_DATE')} ${format(new Date(), 'MMM dd, yyyy')}`}
                className='font-metricR text-bw07 text-[14px]'
              />
            </div>
          </div>
          <Button
            onClick={handleDownload}
            className='px-[24px] py-[8px] bg-bw00 hover:bg-bw01 border border-bw03 rounded-[100px]'
            id='downloadInvoice'
            childElement={
              <div className='flex gap-[4px] items-center'>
                <Download className='stroke-bw07 w-[20px]' />
                <Heading
                  title={t('MAIL_CHECK.SUCCESS.DOWNLOAD')}
                  className='font-metricR text-[14px] text-bw07 leading-[150%]'
                />
              </div>
            }
          />
        </div>
      </div>
      <div className='w-full flex items-center justify-center py-[24px] border-t border-bw03 fadeIn'>
        <div className='flex items-center gap-[12px]'>
          <Button
            onClick={handleMailAnotherCheck}
            className='py-[17px] px-[20px] rounded-[4px] w-[226px] border border-action03 text-action03 text-[16px] bg-bw00 hover:bg-action04Low'
            id='mailAnotherCheck'
            title={t('MAIL_CHECK.SUCCESS.MAIL_ANOTHER_CHECK')}
          />
          <Button
            onClick={handleDone}
            className='py-[17px] px-[40px] rounded-[4px] w-[226px] text-bw00 text-[16px] border border-action03 bg-action03 hover:bg-action04'
            id='doneMailCheck'
            title={t('MAIL_CHECK.SUCCESS.DONE')}
          />
        </div>
      </div>
    </>
  );
};
